export interface VoiceDescriptor {
  name: string;
  lang: string;
  voiceURI?: string;
  localService: boolean;
}

export interface VoiceTraits {
  australian: boolean;
  male: boolean;
  female: boolean;
  highQuality: boolean;
  network: boolean;
}

const HD_VOICE = /natural|neural|premium|enhanced|wavenet|studio|journey|hd/i;
const MALE_VOICE =
  /\bmale\b|william|lee|gordon|james|jack|daniel|ryan|oliver|liam|matthew|andrew/i;
const FEMALE_VOICE =
  /\bfemale\b|natasha|karen|matilda|catherine|helen|olivia|ava|samantha|victoria|zoe/i;

/*
 * Google exposes its Android voices as opaque engine codes. `aub` and `aud`
 * are the two Australian male families; `afh`, `aua`, `auc`, and the generic
 * language voice are female. Treating every unknown en-AU name as "probably
 * male" was why the female voice kept winning even after the UI promised a
 * male one.
 */
const GOOGLE_AU_MALE = /\ben-au-x-(?:aub|aud)(?:\b|-)/i;
const GOOGLE_AU_FEMALE = /\ben-au-(?:language\b|x-(?:afh|aua|auc)(?:\b|-))/i;

function key(v: VoiceDescriptor): string {
  return `${v.name} ${v.voiceURI ?? ''}`.replaceAll('_', '-');
}

export function voiceTraits(v: VoiceDescriptor): VoiceTraits {
  const id = key(v);
  const tag = v.lang.toLowerCase().replace('_', '-');
  const male = GOOGLE_AU_MALE.test(id) || MALE_VOICE.test(id);
  const female = !male && (GOOGLE_AU_FEMALE.test(id) || FEMALE_VOICE.test(id));
  return {
    australian: tag === 'en-au' || /australia|australian|\ben-au(?:\b|-)/i.test(id),
    male,
    female,
    highQuality: HD_VOICE.test(id) || !v.localService,
    network: !v.localService || /\bnetwork\b/i.test(id),
  };
}

/**
 * Pick clarity before convenience: enhanced/network male AU, then any male AU,
 * then a non-female AU fallback. Never select another English accent merely
 * because it has a male voice; a wrong accent is not the requested voice.
 */
export function bestAustralianVoice<T extends VoiceDescriptor>(candidates: T[]): T | null {
  const male = candidates.filter((v) => voiceTraits(v).male);
  return (
    male.find((v) => {
      const traits = voiceTraits(v);
      return traits.highQuality && traits.network;
    }) ??
    male.find((v) => voiceTraits(v).highQuality) ??
    male.find((v) => voiceTraits(v).network) ??
    male[0] ??
    candidates.find((v) => {
      const traits = voiceTraits(v);
      return !traits.female && traits.highQuality && traits.network;
    }) ??
    candidates.find((v) => {
      const traits = voiceTraits(v);
      return !traits.female && traits.highQuality;
    }) ??
    candidates.find((v) => !voiceTraits(v).female) ??
    null
  );
}
